import './LoginForm.scss';
import { classNames } from "primereact/utils"
import { ReactComponent as EmailIcon } from "./assets/emailoutline.svg";
import { ReactComponent as RunwayLogo } from "../../../../assets/JCRRunwayLogo.svg";
import { ReactComponent as KeyIcon } from "./assets/keyvariant.svg";
import { Controller, useForm } from "react-hook-form"
import InputText from "../../../../components/prime-react-overrides/input-text/InputText"
import { FORM_FIELD_KEYS, operationsEmailSchema, operationsOtpSchema } from "./LoginConfig"
import { joiResolver } from "@hookform/resolvers/joi";
import PrimaryButton from "../../../../components/prime-react-overrides/button/Button";
import { useState } from 'react';
import { useLoginJCRUserMutation, useVerifyJCRUserOTPMutation } from '../../../../services/userAuth';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { setUserCredentials } from '../../LoginAuthSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../../config/routes';
import { Button } from 'primereact/button';
import CountDown from 'react-countdown';

const attendancePlatformText = <h2 className='HeadingText text-sm font-medium text-white mt-0 mb-3 uppercase'>Attendance Platform</h2>
function EnterEmail({ onEmailEntered, isLoading }) {

  const { control, formState: { errors }, handleSubmit } = useForm({
    resolver: joiResolver(operationsEmailSchema),
    defaultValues: {
      [FORM_FIELD_KEYS.EMAIL]: ''
    }
  });

  return (
    <form onSubmit={handleSubmit(onEmailEntered)} >
      {attendancePlatformText}
      <label htmlFor={FORM_FIELD_KEYS.EMAIL} className={classNames({ 'p-error': errors[FORM_FIELD_KEYS.EMAIL] }, 'text-white font-semibold text-xl mb-3')}
      >Enter your email</label>
      <p className='opacity-80 text-white text-sm font-medium mb-5'>Please enter your email address for verification and we will proceed to log you in.</p>
      
      <div className="p-fluid field relative">
        <Controller name={FORM_FIELD_KEYS.EMAIL} control={control} render={({ field, fieldState }) => (

          <InputText
            id={field[FORM_FIELD_KEYS.EMAIL]} {...field} className={classNames({ 'p-invalid': fieldState.error }, 'p-inputtext-lg')}
            leftIcon={<EmailIcon />} placeholder="john.smith@jaspercolin.com"
          />

        )} />
        {<small className='p-error font-medium absolute' >{errors[FORM_FIELD_KEYS.EMAIL] && errors[FORM_FIELD_KEYS.EMAIL].message}</small>}
      </div>

      <div className="flex justify-content-center align-items-center">
        <PrimaryButton type='submit' label="Send OTP" loading={isLoading} className='my-6 uppercase' />
      </div>

    </form>
  )
}


function EnterOtp({ onOTPEntered, isVerifying, isSendingMail, onResend }) {
  const { control, formState: { errors }, handleSubmit } = useForm({
    resolver: joiResolver(operationsOtpSchema),
    defaultValues: {
      [FORM_FIELD_KEYS.OTP]: ''
    }
  });

  const handleCountdown = ({ completed, seconds, minutes, api }) => {

    if (completed) {
      return <Button type='button' label="Resend OTP" onClick={(e) => { onResend(e); api.start(); }} loading={isSendingMail} className="mb-3 text-sm font-semibold p-button-danger p-button-text" />
    }

    return <span className='text-sm mb-5 font-semibold ResendText'>Resend OTP <span className='text-white'> in {minutes} : {seconds}s</span></span>
  }
  return (
    <form onSubmit={handleSubmit(onOTPEntered)} >

      {attendancePlatformText}
      <label htmlFor={FORM_FIELD_KEYS.OTP} className={classNames({ 'p-error': errors[FORM_FIELD_KEYS.OTP] }, 'text-white font-semibold text-xl mb-3')}>Enter OTP</label>
      <p className='opacity-80 text-white text-sm font-medium mb-5'>Protecting your account is our top priority. Please confirm your account by entering the verification code send to your email.</p>

      <div className="p-fluid field relative">
        <Controller name={FORM_FIELD_KEYS.OTP} control={control} render={({ field, fieldState }) => (
          <InputText
            id={field[FORM_FIELD_KEYS.OTP]} {...field} className={classNames({ 'p-invalid': fieldState.error }, "p-inputtext-lg")}
            leftIcon={<KeyIcon />} feedback={false} placeholder="* * * * * * * * *" type="password"
          />

        )} />
        <small className='p-error font-medium absolute'>{errors[FORM_FIELD_KEYS.OTP] && errors[FORM_FIELD_KEYS.OTP].message}</small>
      </div>

      <div className="flex flex-column justify-content-center align-items-center">
        <PrimaryButton type='submit' loading={isVerifying} label="Login" className='my-4 uppercase' />
        <CountDown date={Date.now() + (60000 * 2)} renderer={handleCountdown} />
      </div>
    </form>
  )
}

// Main Component
function LoginForm() {
  const [otpSent, setOtpSent] = useState(false)
  const [email, setEmail] = useState(null)
  const [loginJCRUser, { isLoading }] = useLoginJCRUserMutation()
  const [verifyOtpJCRUser, { isLoading: isVerifying }] = useVerifyJCRUserOTPMutation()
  const { state } = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const handleEmailEntered = async ({ email }) => {

    const response = await loginJCRUser({ email }).unwrap()

    if (response.emailSent) {
      setOtpSent(true)
      toast.success('OTP sent on your E-Mail', { icon: '👍' })
      setEmail(email)
    }

  }
  const handleOTPEntered = async ({ otp }) => {
    const loginResponse = await verifyOtpJCRUser({ otp, email }).unwrap()
    if (loginResponse?.user) {
      const { user } = loginResponse
      dispatch(setUserCredentials({ user }))
      const pathname = state?.from?.pathname ?? ROUTES.HOME
      navigate(pathname)
    }
  }

  let content = null;

  if (!otpSent) {
    content = <EnterEmail onEmailEntered={handleEmailEntered} isLoading={isLoading} />
  }
  else {
    content = <EnterOtp onOTPEntered={handleOTPEntered} isSendingMail={isLoading} isVerifying={isVerifying} onResend={() => handleEmailEntered({ email })} />
  }

  return (
    <section className="LoginForm">
      <div className='w-full px-4 md:px-7'>
        <div className="flex flex-column justify-content-center align-items-center py-6">
          <RunwayLogo className="RunwayLogo" />
        </div>
        {content}
      </div>
    </section>
  )
}

export default LoginForm